import React, { useState } from "react";
import { ShieldCheck, BookOpen, Award, Phone, Video, Smartphone } from "lucide-react";
import { sendOtp, verifyOtp } from "../../api/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { storeAuthData } from "../../api/storageService";
import { useNavigate } from "react-router-dom";

const Register = () => {
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const handleSendOtp = async () => {
        if (!phone || phone.length !== 10) {
            toast.error("Enter valid 10 digit phone number");
            return;
        }

        try {
            setLoading(true);

            const res = await sendOtp(phone, "weblogin");

            if (res.status) {
                setOtpSent(true);
                toast.success("OTP sent successfully ✅"); // 👈 toaster
            } else {
                toast.error(res.message || "Failed to send OTP");
            }

        } catch (err) {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp) {
            toast.error("Enter OTP");
            return;
        }

        try {
            setLoading(true);

            const res = await verifyOtp(phone, otp, "weblogin");

            if (res.status) {
                storeAuthData(res);
                toast.success("Login successful 🎉"); // 👈 toaster

                console.log("Login success:", res.data);

                navigate("/dashboard");

            } else {
                toast.error("Invalid OTP");
            }

        } catch (err) {
            toast.error("OTP verification failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <><ToastContainer position="top-right" autoClose={3000} />
            <div className="custom-science custom-padding pt-80 pb-80 xl-screen-padding">
                <div className="w-full max-w-6xl grid md:grid-cols-2 gap-12">

                    <div className="hidden md:block">

                        <div className="bg-gray-100 text-gray-800 font-lato text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-2 mb-6">
                            <ShieldCheck size={14} className="text-[#5bb532]" />
                            Secure student access for Nutrition Academy
                        </div>
                        <h1 className="text-4xl font-bold text-gray-900 leading-tight mb-4 font-lato font-bold ">
                            Continue your learning journey with a focused, trusted login experience.
                        </h1>

                        <p className="text-gray-500 mb-8 font-lato">
                            Sign in to access purchased courses, live classes, quiz history,
                            certificates, saved resources, and your personalized dashboard.
                        </p>
                        <div className="grid grid-cols-2 gap-4 mb-8 font-lato">
                            {[
                                { icon: <BookOpen size={18} />, title: "Your learning dashboard", desc: "Track progress and revisit lessons." },
                                { icon: <Award size={18} />, title: "Certificates and achievements", desc: "Access verified certificates." },
                                { icon: <Video size={18} />, title: "Live class access", desc: "Join sessions anytime." },
                                { icon: <Smartphone size={18} />, title: "Synced across devices", desc: "Continue learning anywhere." },
                            ].map((item, i) => (
                                <div key={i} className="bg-[#f8f9fa] border rounded-xl p-4 font-lato">
                                    <div className="text-green-600 mb-2 font-lato">{item.icon}</div>
                                    <h4 className="text-md font-semibold text-gray-800 font-lato">{item.title}</h4>
                                    <p className="text-sm text-gray-500 font-lato">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-4">
                            {["12k+", "4.9/5", "24/7"].map((val, i) => (
                                <div key={i} className="bg-white border rounded-xl px-5 py-3 text-left">
                                    <p className="font-bold text-xl text-gray-900">{val}</p>
                                    <span className="text-xs text-gray-500">
                                        {i === 0 ? "Learners" : i === 1 ? "Rating" : "Support"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-center font-lato">

                        <div className="w-full max-w-md bg-white rounded-2xl p-8">

                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Create your account
                            </h2>

                            <p className="text-sm text-gray-500 mb-6">
                                Join Nutrition Academy today and unlock your personalized learning dashboard.
                            </p>


                            <div className="text-center text-xs text-gray-400 mb-5 relative">
                                <span className="bg-white px-3 relative z-10">
                                    Sign up with phone
                                </span>

                                <div className="absolute top-1/2 left-0 w-full h-px bg-gray-200"></div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-4">

                                <div>
                                    <p className="text-xs font-semibold text-gray-600 mb-2">
                                        First Name
                                    </p>

                                    <input
                                        placeholder="Enter first name"
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none text-sm"
                                    />
                                </div>


                                <div>
                                    <p className="text-xs font-semibold text-gray-600 mb-2">
                                        Last Name
                                    </p>

                                    <input
                                        placeholder="Enter last name"
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none text-sm"
                                    />
                                </div>

                            </div>
                            <p className="text-xs font-semibold text-gray-600 mb-2">
                                Phone number
                            </p>

                            <div className="relative mb-4">

                                <input
                                    value={phone}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                        setPhone(val)
                                    }}
                                    placeholder="+91 98765 43210"
                                    className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 outline-none text-sm"
                                />

                                <Phone
                                    size={18}
                                    className="absolute right-4 top-3.5 text-green-600"
                                />

                            </div>

                            <button
                                onClick={handleSendOtp}
                                className="w-full py-3 rounded-full bg-gray-100 text-gray-700 text-sm font-semibold mb-4"
                            >
                                💬 Send OTP
                            </button>
                            <div className="flex justify-between mb-2">

                                <p className="text-xs font-semibold text-gray-600">
                                    Enter OTP
                                </p>

                                <span className="text-xs text-green-600 font-semibold">
                                    Resend code
                                </span>

                            </div>
                            <div className="relative mb-4">

                                <input
                                    value={otp}
                                    onChange={(e) => {
                                        setOtp(
                                            e.target.value.replace(/\D/g, "").slice(0, 6)
                                        )
                                    }}
                                    placeholder="••••••"
                                    className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 outline-none text-sm tracking-widest"
                                />
                                 <ShieldCheck
                                    size={18}
                                    className="absolute right-4 top-3.5 text-green-600"
                                />

                            </div>
                            <div className="flex gap-2 items-center text-xs text-gray-500 mb-5">

                                <span className="bg-green-600 text-white rounded-full w-3 h-3 flex items-center justify-center">
                                    ✓
                                </span>

                                I agree to the
                                <span className="text-green-600 font-semibold">
                                    Terms of Service
                                </span>
                                and
                                <span className="text-green-600 font-semibold">
                                    Privacy Policy
                                </span>

                            </div>
                            <div className="flex justify-between text-xs text-gray-400 mb-5">

                                <span>
                                    OTP valid for 15 minutes
                                </span>
  <span className="text-green-600 bg-green-50 px-3 py-1 rounded-full">
                                    🛡 Secure sign up
                                </span>
                            </div>
                            <button
                                onClick={handleVerifyOtp}
                                className="w-full bg-[#15803d] text-white py-3 rounded-xl font-bold"
                            >
                                Create Account →
                            </button>
                            <p className="text-center text-sm text-gray-500 mt-5">         Already have an account?
                                <span className="text-green-600 font-semibold ml-1">
                                    Log In
                                </span>

                            </p>


                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default Register;